import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Video, Square, Loader2, Copy, ExternalLink, Trash2, Mic, MicOff, Monitor, CameraOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIME = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'].find((m) => {
  try { return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m); } catch { return false; }
}) || 'video/webm';

export default function AdminVideoSessions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('screen'); // 'screen' | 'camera' | 'screen+mic'
  const [withAudio, setWithAudio] = useState(true);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [uploading, setUploading] = useState(false);

  const mediaRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const previewVideoRef = useRef(null);
  const playbackRef = useRef(null);
  const timerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/video-sessions`, { withCredentials: true });
      setItems(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load video sessions');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    setBlob(null);
    chunksRef.current = [];
    try {
      let stream;
      if (mode === 'camera') {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: withAudio });
      } else if (mode === 'screen+mic') {
        const screen = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
        let micTrack = null;
        if (withAudio) {
          try {
            const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
            micTrack = mic.getAudioTracks()[0];
          } catch { /* ignore */ }
        }
        stream = new MediaStream([...screen.getVideoTracks(), ...(micTrack ? [micTrack] : [])]);
      } else {
        stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: withAudio });
      }
      mediaRef.current = stream;
      if (previewVideoRef.current) {
        previewVideoRef.current.srcObject = stream;
        previewVideoRef.current.muted = true;
        previewVideoRef.current.play().catch(() => {});
      }
      const recorder = new MediaRecorder(stream, { mimeType: MIME });
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const b = new Blob(chunksRef.current, { type: MIME });
        setBlob(b);
        stream.getTracks().forEach((t) => t.stop());
        if (previewVideoRef.current) previewVideoRef.current.srcObject = null;
      };
      // If user stops sharing via browser UI, end the recording
      stream.getVideoTracks()[0]?.addEventListener('ended', () => {
        if (recorderRef.current && recorderRef.current.state === 'recording') recorderRef.current.stop();
      });
      recorder.start(500);
      recorderRef.current = recorder;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      toast.error(err.message || 'Could not start recording');
    }
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const upload = async () => {
    if (!blob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      const ext = MIME.includes('mp4') ? '.mp4' : '.webm';
      fd.append('file', blob, `session-${Date.now()}${ext}`);
      fd.append('title', title || `Session ${new Date().toLocaleString()}`);
      fd.append('description', description);
      fd.append('recipient_email', recipientEmail);
      const res = await axios.post(`${API_URL}/api/video-sessions`, fd, { withCredentials: true });
      toast.success('Uploaded. Share link copied.');
      try { await navigator.clipboard.writeText(res.data.share_url); } catch { /* */ }
      setBlob(null); setTitle(''); setDescription(''); setRecipientEmail('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (sid) => {
    if (!window.confirm('Delete this recording?')) return;
    try {
      await axios.delete(`${API_URL}/api/video-sessions/${sid}`, { withCredentials: true });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const fmtBytes = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;

  return (
    <AdminLayout title="Video Sessions">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1D4ED8] text-white rounded-xl p-6">
          <h1 className="text-2xl font-bold mb-1" data-testid="video-sessions-title">Loom-style Video Sessions</h1>
          <p className="text-white/85 text-sm">Record your screen, camera, or both, and share a private link with a client in seconds. Recordings stay on your DCA Enterprises domain.</p>
        </div>

        {/* Recorder */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 lg:p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-[#F1F5F9] rounded-md p-1">
              {[
                { v: 'screen', label: 'Screen', icon: Monitor },
                { v: 'camera', label: 'Camera', icon: Video },
                { v: 'screen+mic', label: 'Screen + Mic', icon: Mic },
              ].map((opt) => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.v}
                    onClick={() => setMode(opt.v)}
                    disabled={recording}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded ${mode === opt.v ? 'bg-white text-[#1D4ED8] shadow-sm' : 'text-[#475569]'}`}
                    data-testid={`vs-mode-${opt.v.replace('+', '-')}`}
                  >
                    <Icon size={12} /> {opt.label}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 text-sm text-[#475569] cursor-pointer">
              <input type="checkbox" checked={withAudio} onChange={(e) => setWithAudio(e.target.checked)} disabled={recording} data-testid="vs-audio" />
              {withAudio ? <Mic size={14} className="text-[#1D4ED8]" /> : <MicOff size={14} className="text-[#94A3B8]" />} Include audio
            </label>
          </div>

          <div className="aspect-video bg-[#0F172A] rounded-lg overflow-hidden flex items-center justify-center relative">
            {recording ? (
              <video ref={previewVideoRef} className="w-full h-full object-contain" />
            ) : blob ? (
              <video ref={playbackRef} src={URL.createObjectURL(blob)} controls className="w-full h-full object-contain" data-testid="vs-playback" />
            ) : (
              <div className="text-center text-white/60 text-sm">
                <CameraOff size={32} className="mx-auto mb-2 opacity-50" />
                Click <strong>Start Recording</strong> below to begin.
              </div>
            )}
            {recording && (
              <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-bold">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span> REC {fmt(elapsed)}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {!recording && !blob && (
              <button onClick={start} className="btn-primary-orange inline-flex items-center gap-2" data-testid="vs-start">
                <Video size={16} /> Start Recording
              </button>
            )}
            {recording && (
              <button onClick={stop} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-md font-bold text-sm inline-flex items-center gap-2" data-testid="vs-stop">
                <Square size={14} /> Stop
              </button>
            )}
            {blob && !recording && (
              <>
                <span className="text-xs text-[#475569]">{fmtBytes(blob.size)} · {MIME}</span>
                <button onClick={() => setBlob(null)} className="btn-outline text-sm" data-testid="vs-discard">Discard</button>
              </>
            )}
          </div>

          {blob && (
            <div className="grid md:grid-cols-2 gap-3">
              <input className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" placeholder="Title (e.g. Walkthrough for Sarah)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="vs-title" />
              <input type="email" className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" placeholder="Recipient email (optional — auto-sends a watch link)" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} data-testid="vs-recipient" />
              <textarea className="md:col-span-2 px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" rows={2} maxLength={1000} placeholder="Short description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="vs-description" />
              <button onClick={upload} disabled={uploading} className="btn-primary-blue md:col-span-2 inline-flex items-center justify-center gap-2" data-testid="vs-upload">
                {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : 'Upload & Get Share Link'}
              </button>
            </div>
          )}
        </div>

        {/* List of past recordings */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
            <h2 className="font-bold text-[#0A0F1C]">Recent recordings ({items.length})</h2>
          </div>
          {loading ? (
            <div className="text-center py-12 text-[#94A3B8]"><Loader2 size={16} className="inline animate-spin mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-[#94A3B8] text-sm">No recordings yet. Record your first session above.</div>
          ) : (
            <div className="divide-y divide-[#F1F5F9]">
              {items.map((it) => (
                <div key={it.session_id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`vs-row-${it.session_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0A0F1C] truncate">{it.title}</div>
                    {it.description && <div className="text-xs text-[#475569] mt-0.5 truncate">{it.description}</div>}
                    <div className="text-[11px] text-[#94A3B8] mt-1">{fmtBytes(it.size_bytes || 0)} · {it.view_count || 0} views · {(it.created_at || '').slice(0, 10)}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={async () => { try { await navigator.clipboard.writeText(it.share_url); toast.success('Share link copied'); } catch { /* */ } }} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`vs-copy-${it.session_id}`}><Copy size={12} /> Copy Link</button>
                    <a href={it.share_url} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`vs-open-${it.session_id}`}><ExternalLink size={12} /> Open</a>
                    <button onClick={() => remove(it.session_id)} className="text-red-600 hover:bg-red-50 p-2 rounded" data-testid={`vs-delete-${it.session_id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
